import { useState, useEffect, useLayoutEffect, useRef, Suspense, type CSSProperties } from "react";
import {
  Clock,
  AlertTriangle,
  Crown,
  Plus,
  Minus
} from "lucide-react";
import { useSearchParams, Outlet, useLocation, matchPath } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import { cn } from "@/lib/utils";
import { PANE_DIVIDER_CLASS } from "@/components/layout/paneDivider";
import { useEdgeResize } from "@/hooks/useEdgeResize";
import AppSidebar from "./layout/AppSidebar";
import MobileNavigation from "./layout/MobileNavigation";
import { BanniereNouvelleVersion } from "./layout/NouvelleVersionBanner";
import { useSidebarLayout, SIDEBAR_COLLAPSED_WIDTH } from "@/hooks/useSidebarLayout";
import { useUnifiedSubscription } from "@/hooks/useUnifiedSubscription";
import { useCabinetSubscription, useCurrentCabinetId } from "@/hooks/useCabinetSubscription";
import { useCabinetMembers } from "@/hooks/cabinet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import NewCabinetsDialog from "./NewCabinetsDialog";
import PendingInvitationsDialog from "./PendingInvitationsDialog";
import { DocumentPreviewPanel } from "@/components/documents/DocumentPreviewPanel";
import { selectPreviewPanelIsOpen, usePreviewPanelStore } from "@/stores/previewPanelStore";
import {
  PREVIEW_PANEL_MAX_WIDTH,
  PREVIEW_PANEL_MIN_WIDTH,
  usePreviewPanelLayout,
} from "@/hooks/usePreviewPanelLayout";
import ManualPaywallOverlay from "@/components/paywall/ManualPaywallOverlay";
import { BillingBanner } from "@/components/billing/BillingBanner";
import { BillingWelcomeDialog } from "@/components/billing/BillingWelcomeDialog";
import { welcomeArrivalFromSearch } from "@/components/billing/welcomeDialog";
import { AnnouncementGate } from "@/features/product-announcements/AnnouncementGate";
import { OnboardingTourGate } from "@/features/onboarding-tour/OnboardingTourGate";
import { WebSocketPoolProvider } from "@/features/hector-agent-casediscovery-v3/providers/WebSocketPoolProvider";
import { DossierCaseDiscoveryShell } from "@/features/hector-agent-casediscovery-v3/components/DossierCaseDiscoveryShell";
import { selectIsCaselessAssistantDockOpen, useAssistantDockStore } from "@/stores/assistantDockStore";
import { CanvasResidence } from "@/features/redaction/components/CanvasResidence";
import { RedactionNavigationGuards } from "@/features/redaction/components/RedactionNavigationGuards";
import { BackgroundSessionNotifier } from "@/features/hector-agent-casediscovery-v3/components/BackgroundSessionNotifier";

// Permission system interfaces
interface AdminContact {
  name: string;
  email: string;
  isActive: boolean;
}

interface UserPermissions {
  canManageSubscription: boolean;
  canViewBilling: boolean;
  canSelectLicenses: boolean;
  canContactAdmins: boolean;
  adminContacts: AdminContact[];
}


const DashboardLayout = () => {
  const location = useLocation();
  const { user, signOut } = useAuth();
  // Un seul emplacement latéral depuis le 2026-09-15 : pièces et arrêts du corpus
  // JP sont des onglets du même panneau, donc un seul prédicat d'ouverture (il y
  // en avait deux, à tenir d'accord, tant que le viewer JP était un second panneau).
  const isSidePanelOpen = usePreviewPanelStore(selectPreviewPanelIsOpen);
  const previewWidth = usePreviewPanelLayout((state) => state.width);
  const setPreviewWidth = usePreviewPanelLayout((state) => state.setWidth);
  // Le drag vit ICI, pas dans le panneau : le séparateur appartient à ce qui
  // contient les DEUX côtés. Ancré à droite, la largeur est ce qui reste entre le
  // curseur et le bord droit de la fenêtre ; le clamp est dans le store.
  const { resizeHandleProps: previewResizeHandleProps } = useEdgeResize({
    side: "right",
    onWidthChange: setPreviewWidth,
  });

  // `--preview-w` : largeur réellement occupée par l'emplacement latéral droit.
  //
  // WHY sur `document.documentElement` et non sur la racine du layout, contrairement
  // à `--sb-w` et `--content-gutter` : ses consommateurs ne sont PAS des frères du
  // contenu. Le wizard bordereau et la dialog de renumérotation sont des portails
  // Radix montés sur `document.body`, donc en dehors de `#root` — ils n'hériteraient
  // jamais d'une variable posée plus bas. Un seul écrivain, nettoyé au démontage.
  //
  // Le `clamp()` vit DANS la valeur : la variable vaut donc toujours la largeur
  // rendue, y compris après un redimensionnement de la fenêtre, sans écouter
  // `resize` ni re-rendre. `--preview-w-half` existe parce qu'une valeur arbitraire
  // Tailwind ne peut pas contenir de `/` (lu comme séparateur d'opacité).
  //
  // ⚠️ `useLayoutEffect` et NON `useEffect` : un effet ordinaire s'exécute après le
  // paint. À l'ouverture d'un aperçu, le panneau était donc peint une image avec
  // l'ancienne largeur — 0 — et le trait de séparation, ancré sur son bord gauche,
  // apparaissait collé au bord droit de l'écran avant de sauter à sa place. Même
  // décalage d'une image à chaque mouvement pendant un redimensionnement.
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      "--preview-w",
      isSidePanelOpen ? `clamp(${PREVIEW_PANEL_MIN_WIDTH}px, ${previewWidth}px, min(60vw, ${PREVIEW_PANEL_MAX_WIDTH}px))` : "0px",
    );
    root.style.setProperty("--preview-w-half", isSidePanelOpen ? "calc(var(--preview-w) / 2)" : "0px");
    return () => {
      root.style.removeProperty("--preview-w");
      root.style.removeProperty("--preview-w-half");
    };
  }, [isSidePanelOpen, previewWidth]);

  // --- Assistant docké ------------------------------------------------------
  // Deux points de montage, exclusifs par le `caseId` du store : le dock d'un
  // dossier vit dans le split redimensionnable de `CaseView` (le contenu du
  // dossier doit rétrécir avec lui) ; le dock HORS dossier vit ici, dans le
  // shell d'app, pour les pages de type réglages qui n'ont pas de split.
  const isAssistantDockOpen = useAssistantDockStore((s) => s.isOpen);
  const assistantDockCaseId = useAssistantDockStore((s) => s.caseId);
  const assistantDockPlacement = useAssistantDockStore((s) => s.placement);
  const closeAssistantDock = useAssistantDockStore((s) => s.close);
  const setAssistantDockPlacement = useAssistantDockStore((s) => s.setPlacement);
  // Prédicat partagé avec les barres « Demander à Hector » des écrans de
  // Personnalisation, qui s'effacent tant que ce dock est monté : une seule
  // définition, sinon les deux lectures peuvent diverger en silence.
  const isCaselessAssistantDockOpen = useAssistantDockStore(selectIsCaselessAssistantDockOpen);

  // Frontière de contexte : un dock ouvert pour le dossier A ne doit jamais
  // rester visible après une navigation SPA — ni vers B, ni hors des dossiers.
  // La garde vivait dans `CaseView`, qui se démonte en quittant la page et ne
  // pouvait donc plus rien nettoyer une fois l'état sorti de son `useState`.
  // Le dock hors dossier n'a lui aucune frontière : il suit l'avocat.
  useEffect(() => {
    if (!isAssistantDockOpen || assistantDockCaseId === null) return;
    const routeCaseId = matchPath({ path: '/cases/:id', end: true }, location.pathname)?.params.id;
    if (routeCaseId === assistantDockCaseId) return;
    closeAssistantDock();
  }, [assistantDockCaseId, closeAssistantDock, isAssistantDockOpen, location.pathname]);

  // Use cabinet subscription system directly
  const currentCabinetId = useCurrentCabinetId();
  const {
    subscription,
    hasValidSubscription,
    isTrialing,
    isActive,
    isTrialEndingSoon,
    isTrialExpired,
    isNewCabinet: isNewUser,
    redirectToCabinetCheckout,
    isLoading: isLoadingSubscription,
    checkoutLoading,
    licenseUsage,
    // Cabinet-specific dialog states
    isFirstTimeInCabinet,


    isAdmin,
    // Query states for granular loading control

  } = useCabinetSubscription(currentCabinetId ?? undefined);
  
  // Get has_completed_onboarding from subscription data (cabinet-level)
  const hasCompletedOnboarding = subscription?.has_completed_onboarding === true;

  // Stable ref for redirectToCabinetCheckout — inline function changes every render
  const redirectToCabinetCheckoutRef = useRef(redirectToCabinetCheckout);
  redirectToCabinetCheckoutRef.current = redirectToCabinetCheckout;

  // Fallback to unified for compatibility
  const { isLicenseExempt, isLoading: isLicenseExemptLoading } = useUnifiedSubscription();
  
  // Get cabinet members to show admin list for non-admin users
  const { data: cabinetMembers } = useCabinetMembers(currentCabinetId);
  
  // All users use cabinet-level subscription logic
  
  // Compatibility function for old redirectToCheckout calls
  // Get admin contacts for permission system 
  const getAdminContacts = (): AdminContact[] => {
    if (!cabinetMembers) return [];
    
    return cabinetMembers
      .filter(member => member.cabinet_role === 'admin')
      .map(admin => {
        // User identity is now inlined on CabinetMember via users LEFT JOIN (migration 057/058)
        return {
          name: admin.full_name || admin.email || "Administrateur",
          email: admin.email || "",
          isActive: true // Could be enhanced with online status
        };
      })
  };

  // Get admin names for non-admin users (legacy function)
  const getAdminNames = () => {
    const adminContacts = getAdminContacts();
    if (adminContacts.length === 0) return "l'administrateur";
    
    const adminNames = adminContacts.map(admin => admin.name);
    
    if (adminNames.length === 1) return adminNames[0];
    if (adminNames.length === 2) return `${adminNames[0]} et ${adminNames[1]}`;
    
    const lastAdmin = adminNames.pop();
    return `${adminNames.join(', ')} et ${lastAdmin}`;
  };

  // Resolve user permissions based on current user status
  const resolveUserPermissions = (): UserPermissions => {
    const adminContacts = getAdminContacts();
    
    return {
      canManageSubscription: isAdmin && !isLicenseExempt,
      canViewBilling: isAdmin,
      canSelectLicenses: isAdmin,
      canContactAdmins: !isAdmin,
      adminContacts
    };
  };
  
  // Debug logging
  useEffect(() => {
    
  }, [hasValidSubscription, isTrialing, isActive, isTrialExpired, isNewUser, isLicenseExempt, isLicenseExemptLoading, isLoadingSubscription, isFirstTimeInCabinet, hasCompletedOnboarding, currentCabinetId, subscription]);
  
  // Barre latérale gauche : largeur redimensionnable + état replié (persistés).
  // `sidebarResizing` est transitoire (pas persisté) : il coupe la transition de
  // marge du contenu pendant le drag pour qu'il colle au curseur.
  const { width: sidebarWidth, collapsed: sidebarCollapsed, setWidth: setSidebarWidth, toggleCollapsed: toggleSidebar } = useSidebarLayout();
  const [sidebarResizing, setSidebarResizing] = useState(false);

  const [showNoSubDialog, setShowNoSubDialog] = useState(false);
  const [showTrialEndingDialog, setShowTrialEndingDialog] = useState(false);
  const [showTrialExpiredDialog, setShowTrialExpiredDialog] = useState(false);
  const [showAddinRedirectDialog, setShowAddinRedirectDialog] = useState(false);
  const [upgradeQuantity, setUpgradeQuantity] = useState(1);
  
  // Update upgradeQuantity to respect minimum used licenses
  useEffect(() => {
    if (licenseUsage?.used_licenses) {
      setUpgradeQuantity(prev => Math.max(prev, licenseUsage.used_licenses));
    }
  }, [licenseUsage?.used_licenses]);
  const [subscriptionDataReady, setSubscriptionDataReady] = useState(false);
  const [noSubDialogDelay, setNoSubDialogDelay] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Effect to manage subscription data readiness
  useEffect(() => {
    if (!isLoadingSubscription && user) {
      // Add small delay to prevent flashing
      const timer = setTimeout(() => {
        setSubscriptionDataReady(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setSubscriptionDataReady(false);
    }
    return undefined;
  }, [isLoadingSubscription, user]);

  // Effect to manage "No sub" dialog delay to prevent it from showing during subscription transitions
  useEffect(() => {
    if (subscriptionDataReady) {
      // Add a 2-second delay before allowing the "No sub" dialog to show
      // This prevents the dialog from appearing during subscription status transitions (e.g., trialing -> active)
      const timer = setTimeout(() => {
        setNoSubDialogDelay(true);
      }, 2000);
      return () => clearTimeout(timer);
    } else {
      setNoSubDialogDelay(false);
    }
    return undefined;
  }, [subscriptionDataReady]);

  useEffect(() => {
    // Staff users bypass all subscription checks and dialogs
    if (isLicenseExempt && !isLicenseExemptLoading) {
      // Reset all dialog states for staff users
      setShowNoSubDialog(false);
      setShowTrialEndingDialog(false);
      setShowTrialExpiredDialog(false);
      return;
    }
    
    // Don't show any dialogs while staff status is loading
    if (isLicenseExemptLoading) {
      return;
    }
    
    // Don't show any dialogs until subscription data is ready
    if (!subscriptionDataReady) {
      return;
    }
    
    // For new users, give a bit more time for subscription data to load
    if (isNewUser && isLoadingSubscription) return;
    
    // Don't show dialogs for new users who should be redirected to pricing
    // NOTE: Redirect logic moved to App.tsx NewUserRedirect to avoid conflicts
    if (isNewUser) {
      // Hide all dialogs immediately
      setShowNoSubDialog(false);
      setShowTrialEndingDialog(false);
      setShowTrialExpiredDialog(false);
      return;
    }
    
    // Cabinet-level dialog logic
    
    // 2. Pro user success dialog - purely URL-based control
    // Dialog visibility is directly controlled by URL parameter presence
    
    // 3. Trial ending dialog - use precise 10-hour logic for cabinet members
    // Check if trial end date has changed and reset dismissed flag if so
    const currentTrialEndKey = 'currentTrialEndDate';
    const dismissedTrialEndingKey = 'hasDismissedTrialEndingDialog';
    const storedTrialEnd = localStorage.getItem(currentTrialEndKey);
    const currentTrialEnd = subscription?.trial_end;
    
    // Reset dismissed flag if trial end date has changed
    if (currentTrialEnd && storedTrialEnd !== currentTrialEnd) {
      localStorage.removeItem(dismissedTrialEndingKey);
      localStorage.setItem(currentTrialEndKey, currentTrialEnd);
    } else if (currentTrialEnd && !storedTrialEnd) {
      // First time seeing this trial end date, store it
      localStorage.setItem(currentTrialEndKey, currentTrialEnd);
    }
    
    const hasDismissedTrialEnding = localStorage.getItem(dismissedTrialEndingKey) === 'true';
    
    // Debug logging for trial ending logic
    if (subscription?.status === 'trialing') {
      
      
      // Add global function to reset dismissed flag for testing
      if (typeof window !== 'undefined') {
        (window as any).resetTrialDialog = () => {
          localStorage.removeItem(dismissedTrialEndingKey);
        };
        
        // Add permission debugging
        resolveUserPermissions();
        
      }
    }
    
    // Trial ending dialog - cabinet-level logic only
    if (isTrialEndingSoon && !hasDismissedTrialEnding && !isTrialExpired) {
      setShowTrialEndingDialog(true);
    }
    
    // 4. No subscription dialog - DISABLED (no subscription required)
    // Original logic disabled - all users have access without subscription
    const shouldShowNoSubDialog = false;
    // const shouldShowNoSubDialog = 
    //   !hasValidSubscription && 
    //   !isTrialing && 
    //   !isActive && 
    //   !isLicenseExempt && 
    //   !isLicenseExemptLoading && // Don't show while staff status is loading
    //   !isLoadingSubscription &&
    //   !subscriptionQuery.isFetching && // Don't show while actively fetching
    //   !subscriptionQuery.isRefetching && // Don't show during background refetch
    //   !isTrialExpired && // Don't show if trial just expired (use trial expired dialog instead)
    //   noSubDialogDelay && // Only show after delay to prevent flashing during status transitions
    //   subscriptionQuery.isFetched && // Only show after initial fetch is complete
    //   (!subscription || (subscription.status !== 'trialing' && subscription.status !== 'active'));
    
    if (shouldShowNoSubDialog) {
      
      setShowNoSubDialog(true);
    } else {
      // Reset dialog if conditions are no longer met
      setShowNoSubDialog(false);
    }
    
    // 5. Trial expired dialog - DISABLED (no subscription required)
    // if (isTrialExpired) {
    //   setShowTrialExpiredDialog(true);
    // }

    // Handle add-in redirect (only for non-staff). Price id + return URLs are
    // resolved server-side by POST /api/stripe/create-checkout-session.
    if (searchParams.get('addin')) {
      setShowAddinRedirectDialog(true);
      redirectToCabinetCheckoutRef.current(1);
      searchParams.delete('addin');
      setSearchParams(searchParams);
    }
  }, [
    isLicenseExempt, 
    isLicenseExemptLoading,
    subscriptionDataReady,
    noSubDialogDelay,
    isLoadingSubscription, 
    user, 
    hasValidSubscription, 
    isTrialing,
    isActive,
    isTrialEndingSoon, 
    isTrialExpired,
    isNewUser,
    isFirstTimeInCabinet,
    subscription?.status,
    subscription?.trial_end,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- read specific params, not the unstable URLSearchParams object
    searchParams.get('addin'),
  ]);

  const handleTrialDialogDismiss = (isOpen: boolean) => {
    if (!isOpen) {
      localStorage.setItem('hasDismissedTrialEndingDialog', 'true');
    }
    setShowTrialEndingDialog(isOpen);
  };





  // `--content-gutter` : distance entre le bord droit de la barre latérale et le
  // bord VISIBLE de la fenêtre de page (la carte blanche `rounded-xl` que chaque
  // écran rend). Posée ici parce que ses deux consommateurs sont des frères — le
  // wrapper de contenu, qui en fait son padding, et la poignée de redimensionnement
  // de `AppSidebar`, dont la portée doit atteindre cette même lisière.
  //
  // WHY une valeur UNIQUE, valable pour toutes les routes : la lisière grise/blanche
  // est le seul repère visible du bord de la barre. La faire dépendre de l'écran
  // déplace la fenêtre à chaque navigation entre des pages qui rendent pourtant la
  // même carte, et désaccorde la poignée, qui n'a qu'une géométrie. Une gouttière
  // réglée par écran serait donc une liste d'exceptions à tenir à jour à la main,
  // qu'un écran ajouté ensuite manquerait en silence (signalement produit
  // 2026-09-10 : fenêtre décalée de 16px et barre non redimensionnable sur
  // Personnalisation et Bibliothèque de modèles). Un écran qui veut respirer
  // davantage pose son propre padding DANS sa carte.
  return (
    <div
      style={{ "--content-gutter": "8px" } as CSSProperties}
      // `100dvh` et non `h-screen` (100vh) : sur téléphone, 100vh compte la hauteur
      // SANS barre d'adresse ni clavier, et comme cette racine ne défile pas, le bas
      // de page (barre de saisie de l'assistant) restait caché dessous.
      className="h-[100dvh] bg-[#fafafa] flex overflow-hidden"
    >
      {/* Desktop Sidebar - fixed position, largeur redimensionnable + repliable */}
      <AppSidebar
        onSignOut={signOut}
        width={sidebarWidth}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebar}
        onWidthChange={setSidebarWidth}
        onResizingChange={setSidebarResizing}
      />

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-grey-200 px-3 py-2 flex items-center justify-center">
        <div className="flex items-center gap-1">
          <img src="/logo_hector-black.png" alt="Hector" className="h-5 w-auto" />
        </div>
      </div>
      
      {/* Mobile Bottom Navigation */}
      <MobileNavigation />
      
      {/* Main content area with sidebar offset - shrinks when preview panel is open.
          La marge gauche (md+) suit la largeur de la barre via la variable CSS
          `--sb-w` : largeur du rail quand replié, largeur (redimensionnable) sinon.
          Pendant le drag, on coupe la transition pour que le contenu colle au curseur. */}
      <div
        style={{ "--sb-w": sidebarCollapsed ? `${SIDEBAR_COLLAPSED_WIDTH}px` : `${sidebarWidth}px` } as CSSProperties}
        className={`flex-1 flex flex-col md:ml-[var(--sb-w)] overflow-hidden pt-12 md:pt-0 ${sidebarResizing ? '' : 'transition-[margin] duration-300 ease-in-out'}`}>
        <BanniereNouvelleVersion />
<Dialog open={showNoSubDialog} onOpenChange={setShowNoSubDialog}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} hideCloseButton={true} className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader className="items-center text-center">
            <Crown className="h-12 w-12 text-grey-600 mb-2" />
            <DialogTitle className="text-base font-medium">
              Pas d'abonnement cabinet
            </DialogTitle>
            <DialogDescription className="px-4 py-2">
              Veuillez contacter le support pour activer votre abonnement et continuer à utiliser Hector.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter className="flex flex-col gap-3 items-stretch pt-4">
            <Button 
              onClick={() => window.open('mailto:contact@hector.legal?subject=Activation abonnement cabinet', '_blank')}
              className="w-full bg-charcoal hover:bg-charcoal-medium"
            >
              Contacter le support
            </Button>
            <Button 
              onClick={signOut} 
              variant="outline"
              className="w-full bg-transparent border-charcoal/30 text-charcoal hover:bg-charcoal/5 hover:border-charcoal/50"
            >
              Se déconnecter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showTrialEndingDialog} onOpenChange={handleTrialDialogDismiss}>
        <DialogContent className="border-none sm:max-w-lg max-h-[85vh] overflow-y-auto" style={{ maxWidth: isAdmin ? '32rem' : '28rem' }}>
          <DialogHeader className="items-center text-center">
            <Clock className="h-12 w-12 text-grey-600 mb-2" />
            <DialogTitle className="text-base font-medium">
              Votre essai prend bientôt fin !
            </DialogTitle>
            <DialogDescription className="px-4 py-2">
              {isAdmin
                ? 'Il vous reste moins de 24 heures pour profiter de toutes les fonctionnalités de votre cabinet. Choisissez le nombre de licences et souscrivez pour continuer.'
                : `Il vous reste moins de 24 heures pour profiter de toutes les fonctionnalités de votre cabinet. Contactez ${getAdminNames()} pour renouveler l'abonnement.`
              }
            </DialogDescription>
          </DialogHeader>
          
          {/* License Selection - Only for Admin Users */}
          {isAdmin && (
            <div className="px-6 py-4">
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Nombre de licences</h4>
                    <div className="flex items-center gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setUpgradeQuantity(Math.max(licenseUsage?.used_licenses || 1, upgradeQuantity - 1))}
                        className="h-8 w-8 p-0"
                        disabled={upgradeQuantity <= (licenseUsage?.used_licenses || 1)}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <div className="flex items-center justify-center min-w-[3rem] h-8 px-3 bg-grey-50 border rounded-md">
                        <span className="font-medium">{upgradeQuantity}</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setUpgradeQuantity(Math.min(100, upgradeQuantity + 1))}
                        className="h-8 w-8 p-0"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Pricing Info */}
                  <div className="bg-grey-50 p-3 rounded-md border border-grey-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        {upgradeQuantity} licence{upgradeQuantity > 1 ? 's' : ''} × 40€/mois
                      </span>
                      <div className="text-right">
                        <span className="text-base font-medium text-grey-800">
                          {upgradeQuantity * 40}€
                        </span>
                        <span className="text-sm text-muted-foreground">/mois</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Admin Contact Info - Only for Member Users */}
          {!isAdmin && (
            <div className="px-6 py-4">
              <div className="bg-grey-50 p-4 rounded-md border border-grey-200">
                <h4 className="font-medium text-grey-800 mb-2">
                  Contactez votre administrateur
                </h4>
                <p className="text-sm text-grey-800">
                  Contactez {getAdminNames()} pour renouveler l'abonnement du cabinet.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="sm:justify-center gap-2">
            <Button onClick={() => handleTrialDialogDismiss(false)} variant="outline">
              Plus tard
            </Button>
            {isAdmin ? (
              <Button
                onClick={() => redirectToCabinetCheckout(upgradeQuantity)}
                className="bg-charcoal hover:bg-charcoal-medium"
                disabled={checkoutLoading}
              >
                <Crown className="h-4 w-4 mr-2" />
                {checkoutLoading ? 'Redirection...' : `Confirmer ${upgradeQuantity} licence${upgradeQuantity > 1 ? 's' : ''}`}
              </Button>
            ) : (
              <Button 
                onClick={() => handleTrialDialogDismiss(false)}
                className="bg-charcoal hover:bg-charcoal-medium"
              >
                Compris
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={showTrialExpiredDialog} onOpenChange={setShowTrialExpiredDialog}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} hideCloseButton={true} className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader className="items-center text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mb-2" />
            <DialogTitle className="text-base font-medium">
              Essai cabinet expiré
            </DialogTitle>
            <DialogDescription className="px-4 py-2">
              Votre période d'essai cabinet a expiré. Veuillez contacter le support pour activer votre abonnement et continuer à utiliser Hector.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter className="flex-col items-center pt-4">
            <Button 
              onClick={() => window.open('mailto:contact@hector.legal?subject=Activation abonnement cabinet - Essai expiré', '_blank')}
              className="w-full bg-charcoal hover:bg-charcoal-medium"
            >
              Contacter le support
            </Button>
            <Button onClick={signOut} className="w-full bg-charcoal hover:bg-charcoal-medium mt-2">
              Se déconnecter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Accueil après paiement (`upgrade_success`) ou après le démarrage de
          l'essai (`trial_started`) : pilotée par l'URL, elle propose d'inviter
          l'équipe quand des postes sont libres. */}
      <BillingWelcomeDialog />
      <Dialog open={showAddinRedirectDialog} onOpenChange={setShowAddinRedirectDialog}>
        <DialogContent className="border-none sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader className="items-center text-center">
            <DialogTitle className="text-base font-medium">Redirection vers l'abonnement Pro</DialogTitle>
            <DialogDescription className="px-4 py-2">
              L'onglet pour vous abonner devrait s'ouvrir d'ici quelques secondes. Cliquez sur le bouton ci-dessous si ce n'est pas le cas !
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => redirectToCabinetCheckout(1)} className="w-full bg-charcoal hover:bg-charcoal-medium">
              Ouvrir manuellement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* New Cabinets Notification Dialog */}
      <NewCabinetsDialog />
      
      {/* Pending Cabinet Invitations Dialog */}
      <PendingInvitationsDialog />

      {/* Product Announcements — self-defers behind every blocking/global modal.
          `blocked` couvre les dialogs pilotées par le state local du layout ;
          les overlays autonomes (invitations/new-cabinets/paywall) sont gérés
          dans AnnouncementGate via leurs propres hooks. */}
      <AnnouncementGate blocked={
        showNoSubDialog || showTrialEndingDialog || showTrialExpiredDialog ||
        showAddinRedirectDialog || welcomeArrivalFromSearch(searchParams) !== null
      } />

      {/* Parcours d'onboarding (drapeau `beta_onboarding_tour`). Monté ici car le
          layout survit aux navigations : le parcours traverse `/`, `/cases/:id`
          et `/assistant`. Il se met de lui-même en retrait derrière toute
          fenêtre Radix ouverte ; `blocked` couvre le reste. */}
      <OnboardingTourGate blocked={
        showNoSubDialog || showTrialEndingDialog || showTrialExpiredDialog ||
        showAddinRedirectDialog || welcomeArrivalFromSearch(searchParams) !== null
      } />

        {/* Bandeau facturation — slim, non bloquant, visible sur toutes les
            pages (au-dessus du conteneur scrollable, sous le header mobile
            grâce au pt-12 de la colonne). Fail-open : ne rend rien tant que
            /stripe/status ne signale pas un abonnement non réglé. */}
        {/* Même gouttière que la fenêtre de page ci-dessous, pour que la carte du
            bandeau s'aligne sur elle au pixel. `empty:hidden` : le bandeau ne rend
            souvent rien, et le retrait du haut ne doit alors pas subsister. */}
        <div className={cn("px-[var(--content-gutter)] pt-2 empty:hidden", sidebarCollapsed && "md:pl-0")}>
          <BillingBanner />
        </div>

        <main className="flex-1 overflow-hidden min-h-0 min-w-0 flex flex-col">
          {/* Le provider est remonté d'un cran : il enveloppe désormais la colonne
              de contenu ET le dock hors dossier, qui a besoin du même pool WS que
              les pages. Un provider ne rend aucun DOM — le layout est inchangé. */}
          <WebSocketPoolProvider>
            {/* Le canvas de rédaction habite ICI, pas dans l'écran assistant
                (#856). Il se rend par portail dans un nœud hôte qui ne change
                jamais d'identité : les écrans lui offrent un emplacement
                (`CanvasSlot`), et quitter l'assistant pour consulter un
                dossier ne détruit donc plus le document ouvert. Hors du
                Suspense de la page : le chargement d'un chunk de route ne doit
                pas suspendre un document en cours de rédaction. Dans le
                provider WebSocket : le suivi de streaming a besoin du pool. */}
            <CanvasResidence />
            {/* Garde-fous de navigation pendant une rédaction (#859) : message
                au premier départ d'un fil qui rédige, et avertissement natif à
                la fermeture de l'onglet. Montés dans la coque parce qu'un
                garde logé dans un écran disparaîtrait à l'instant précis où il
                sert — celui où l'avocat s'en va. */}
            <RedactionNavigationGuards />
            {/* L'avertisseur de fin de tour a REJOINT la coque (#859). Il ne
                vivait que dans l'assistant, donc quitter cet écran supprimait
                tout avertissement de fin — ce qui contredit #856/#857, où le
                travail, lui, survit à la navigation. */}
            <BackgroundSessionNotifier />
            {/* Rangée : la page rétrécit quand le dock s'ouvre, elle n'est jamais
                recouverte (même contrat visuel que le split de la page dossier). */}
            <div className="flex-1 min-h-0 min-w-0 flex flex-row overflow-hidden">
              <div className={cn("flex-1 min-h-0 min-w-0 px-[var(--content-gutter)] py-2 pb-16 md:pb-2", sidebarCollapsed && "md:pl-0")}>
                {/* LA FENÊTRE DE PAGE — carte blanche à filet et coins arrondis.
                    Rendue ICI depuis le 2026-09-15, et non plus par chaque écran
                    via `PageWindow` : elle doit contenir le contenu de la page ET
                    l'emplacement d'aperçu, séparés par un trait fin, comme le
                    canvas de rédaction et « Demander à Hector » le sont déjà dans
                    la fenêtre de leur écran. Rendue par la page, la carte se
                    fermait avant l'aperçu, qui se lisait alors comme une seconde
                    surface posée à côté (signalement produit 2026-09-15).

                    Son bord gauche reste le SEUL repère visible du bord de la
                    barre latérale : la poignée d'`AppSidebar` se cale dessus via
                    `--content-gutter`. Ne pas y toucher sans relire cette
                    géométrie (cf. `components/CLAUDE.md`). */}
                <div className="flex h-full min-h-0 flex-row overflow-hidden rounded-xl border border-grey-200 bg-white">
                  <div className="min-h-0 min-w-0 flex-1 overflow-y-auto [overflow-wrap:break-word]">
                    {/* Layout route : le contenu de page vient de l'<Outlet/>. Suspense
                        interne = le chunk lazy de la page se charge SANS démonter le
                        shell (sidebar + WS restent montés) — seul le contenu affiche un
                        bref voile de chargement. (#238, Batch B code-splitting) */}
                    <Suspense fallback={
                      <div className="h-full flex items-center justify-center">
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-grey-300 border-t-charcoal" />
                      </div>
                    }>
                      <Outlet />
                    </Suspense>
                  </div>

                  {/* La démarcation : un trait fin qu'on attrape pour redimensionner,
                      exactement comme entre « Demander à Hector » et le reste.
                      C'est un ÉLÉMENT DE LA RANGÉE, qui occupe son pixel entre les
                      deux zones — et non un absolu posé sur le bord du panneau, où
                      il se confondait avec lui et ne se voyait plus. Il n'existe
                      qu'à partir de md : tirer une colonne n'a pas de sens sur
                      390 px de large. */}
                  {isSidePanelOpen && (
                    <div
                      role="separator"
                      aria-orientation="vertical"
                      aria-label="Redimensionner l'aperçu"
                      {...previewResizeHandleProps}
                      className={cn(PANE_DIVIDER_CLASS, "hidden shrink-0 cursor-col-resize md:flex")}
                    />
                  )}

                  {/* Emplacement latéral unique — pièces et arrêts du corpus JP en
                      onglets, DANS la fenêtre, séparé du contenu par ce trait fin. */}
                  <DocumentPreviewPanel />
                </div>
              </div>

              {isCaselessAssistantDockOpen && (
                /* Détaché, le shell se rend en `fixed` : la gouttière doit alors
                   disparaître, sinon le panneau flottant laisse un vide à droite.
                   Largeur alignée sur le panneau du split dossier — 30 %, bornée
                   à 320 px et 42 % — pour que l'avocat retrouve le même objet
                   d'une page à l'autre. Sous `md`, l'assistant prend l'écran
                   comme les autres surfaces plein écran du mobile. */
                <div className={cn(
                  "min-h-0 shrink-0",
                  assistantDockPlacement === "floating"
                    ? "w-0"
                    // `pb-16` : en fenêtre étroite, le dock est hors du conteneur qui
                    // réserve la place de la barre de navigation du bas, qui
                    // recouvrait sinon sa barre de saisie et son bouton d'envoi.
                    : "w-full pb-16 border-l border-grey-200 bg-white md:pb-0 md:w-[30%] md:min-w-[320px] md:max-w-[42%]",
                )}>
                  <DossierCaseDiscoveryShell
                    caseId={null}
                    placement={assistantDockPlacement}
                    onPlacementChange={setAssistantDockPlacement}
                    onClose={closeAssistantDock}
                  />
                </div>
              )}
            </div>
          </WebSocketPoolProvider>
        </main>
      </div>

      {/* Manual paywall — blocks entire UI when activate_paywall is true in cabinet_members */}
      <ManualPaywallOverlay />
    </div>
  );
};

export default DashboardLayout;
